import { z } from 'zod';
import {
  ANALYST_STYLE_RUNS_DEFAULT,
  analystFunctionSchema,
  analystLevelSchema,
  analystPassSchema,
  analystRunSchema,
  analystStyleRunsSchema,
} from './analyst.js';
import { METHOD_STYLE_NAME_MAX } from './method.js';
import { NOYAU_JUSTIFICATION_MAX, noyauCodeSchema } from './noyau.js';

/**
 * THE ANALYSIS of a determination (analyste.md §10, issue #196): the whole
 * reasoning of the two passes, as the determination stores it and the parcours
 * of Sessions, the markdown export and `get_session` serve it - the first pass
 * objective by objective, the three levels and what decided each, the conflicts,
 * the candidates and the eliminated, the compatibility of the six, the style of
 * each candidate with the passages that carry it, and the fingerprint of the
 * instrument.
 *
 * Stored in `session_noyau_determinations.analysis` and
 * `session_noyau_trials.analysis`, one JSON column each, NULL on what was
 * written before #196 - which the parcours reads as « lecture directe, avant
 * #196 » (K19). The shapes inside are new, so they carry read defaults wherever
 * a later lot may add a field (the régime of BUILD-787), and none is `.strict()`.
 *
 * ## What is derived, and so not stored twice
 *
 * The six notes and their sentences are the determination's `scores`; the
 * principal and the second its `principal` and `second`; the note of a candidate
 * follows from its compatibility, its style and the weight of the settings. What
 * is stored here is what none of that can rebuild: what each pass SAW and SAID,
 * and what the code made of it.
 *
 * ## Written whole by one function
 *
 * `analysisOfDetermination` (`noyau/analysis.ts`) assembles it from the results
 * of the pure rules, so a writer never builds one by hand and the invariants hold
 * by construction: six rows in the table's order once the first pass has been
 * read, three levels in the order of a triplet, a candidate always present.
 */

/** A percentage: an integer from 0 to 100. Parts, compatibility, style, notes. */
const percentSchema = z.number().int().min(0).max(100);

/* ------------------------------------------------------ the instrument's header */

/**
 * The FINGERPRINT of the instrument (K11): the first 8 hexadecimal characters of
 * the SHA-256 of the two systems joined by a blank line (`instrumentFingerprintOf`).
 * Two determinations of one fingerprint received the same protocol section, the
 * same Méthode and the same code - what the measure of §16 groups on.
 */
export const noyauInstrumentFingerprintSchema = z.string().regex(/^[0-9a-f]{8}$/);
export type NoyauInstrumentFingerprint = z.infer<typeof noyauInstrumentFingerprintSchema>;

/**
 * The version of Méthode read (§4.5): the one the session froze, or the latest
 * dev of a blank trial - by identifier AND by number, so the parcours writes
 * « Méthode v2 » from the analysis alone, with no other request.
 */
export const analysisMethodSchema = z.object({
  versionId: z.string().min(1),
  number: z.number().int().positive(),
});
export type AnalysisMethod = z.infer<typeof analysisMethodSchema>;

/**
 * The SETTINGS applied (§3.2, §10): the two thresholds, the weight of the
 * style and, since #229, the number of readings of the style, as the frozen
 * section gave them, and the version of Méthode the instrument was built with -
 * null when the session froze none (`failure`). `styleRuns` reads 1 on an analysis
 * written before #229.
 */
export const analysisSettingsSchema = z.object({
  pairThreshold: percentSchema,
  soloThreshold: percentSchema,
  styleWeight: percentSchema,
  styleRuns: analystStyleRunsSchema.default(ANALYST_STYLE_RUNS_DEFAULT),
  method: analysisMethodSchema.nullable().default(null),
});
export type AnalysisSettings = z.infer<typeof analysisSettingsSchema>;

/**
 * Why a determination was settled `failed` WITHOUT a call (§9.2, G22): the
 * session froze no version of Méthode, or a protocol whose Analyste section is
 * not complete - a session started before the delivery. The levels and the
 * passes left empty are named, so the parcours says what was missing.
 *
 * When both hold, `method-missing` is the reason and the gaps of the section are
 * named all the same.
 */
export const ANALYSIS_FAILURE_REASONS = ['method-missing', 'section-incomplete'] as const;
export const analysisFailureReasonSchema = z.enum(ANALYSIS_FAILURE_REASONS);
export type AnalysisFailureReason = z.infer<typeof analysisFailureReasonSchema>;

export const analysisFailureSchema = z.object({
  reason: analysisFailureReasonSchema,
  levelsMissing: z.array(analystLevelSchema).default([]),
  readingsMissing: z.array(analystPassSchema).default([]),
});
export type AnalysisFailure = z.infer<typeof analysisFailureSchema>;

/* ------------------------------------------------------------- the first pass */

/** The three parts of the functions the first pass gave an objective: S, G, P, each 0 to 100, summing to 100 (K6). */
export const analysisSharesSchema = z.object({
  S: percentSchema,
  G: percentSchema,
  P: percentSchema,
});
export type AnalysisShares = z.infer<typeof analysisSharesSchema>;

/**
 * One objective of the first pass (§5.2, §10): its identifier and its NAME at
 * the frozen protocol, the LEVEL the code knew it at - the pass itself never
 * saw it (G10) -, the three parts, and the pass's sentence.
 */
export const analysisFunctionRowSchema = z.object({
  objectiveId: z.string().min(1),
  name: z.string(),
  level: analystLevelSchema,
  shares: analysisSharesSchema,
  justification: z.string().min(1).max(NOYAU_JUSTIFICATION_MAX),
});
export type AnalysisFunctionRow = z.infer<typeof analysisFunctionRowSchema>;

/* ------------------------------------------------------------------ the levels */

/**
 * The five STATES of a level (§6.2, §6.3), stored as identifiers and said by
 * `ANALYSIS_LEVEL_STATE_LABELS`:
 *
 * - `fixed-pair` - two readable objectives or more, each giving at least the
 *   threshold of a pair to the same function: « fixé en symétrie »;
 * - `fixed-solo` - one readable objective giving at least the threshold of an
 *   objective alone to a function: « fixé, objectif seul »;
 * - `not-fixed` - readable objectives, and no function they agree on;
 * - `nothing-to-read` - no readable objective: « rien à lire » (K26);
 * - `dropped` - fixed, then set aside by a conflict with a surer level
 *   (§6.3): « écarté (conflit) ».
 */
export const ANALYSIS_LEVEL_STATES = [
  'fixed-pair',
  'fixed-solo',
  'not-fixed',
  'nothing-to-read',
  'dropped',
] as const;
export const analysisLevelStateSchema = z.enum(ANALYSIS_LEVEL_STATES);
export type AnalysisLevelState = z.infer<typeof analysisLevelStateSchema>;

/** What each state reads as on a screen, the words of the maquette. */
export const ANALYSIS_LEVEL_STATE_LABELS: Readonly<Record<AnalysisLevelState, string>> = {
  'fixed-pair': 'fixé en symétrie',
  'fixed-solo': 'fixé, objectif seul',
  'not-fixed': 'non fixé',
  'nothing-to-read': 'rien à lire',
  dropped: 'écarté (conflit)',
};

/** The two states in which a level constrains the candidates. */
export function isLevelFixed(state: AnalysisLevelState): boolean {
  return state === 'fixed-pair' || state === 'fixed-solo';
}

/**
 * One level, as the code read it (§6.2): its state, the function it is - or, for
 * a dropped level, WAS - fixed on, the threshold that applied, the parts that
 * decided it, and the mean of each function over its readable objectives (the
 * figure the compatibility averages, §6.5).
 *
 * `decisive` lists, for a fixed or dropped level, each readable objective with
 * the part it gave to `function` - « Pourquoi G fixé en symétrie, 72 et 84 » -
 * and nothing for a level that is not fixed. `means` is null when there is
 * nothing to read.
 */
export const analysisLevelSchema = z.object({
  level: analystLevelSchema,
  state: analysisLevelStateSchema,
  function: analystFunctionSchema.nullable().default(null),
  threshold: percentSchema.nullable().default(null),
  decisive: z.array(z.object({ objectiveId: z.string().min(1), share: percentSchema })).default([]),
  means: z
    .object({
      S: z.number().min(0).max(100),
      G: z.number().min(0).max(100),
      P: z.number().min(0).max(100),
    })
    .nullable()
    .default(null),
});
export type AnalysisLevel = z.infer<typeof analysisLevelSchema>;

/**
 * Which rule of K4 decided a conflict, in the order they are tried: a level fixed
 * in symmetry beats an objective alone (`symmetry`); then the highest of the
 * smallest parts of the function among a level's objectives (`floor`); then the
 * order Pourquoi, Comment, Quoi (`order`).
 */
export const ANALYSIS_CONFLICT_DECIDERS = ['symmetry', 'floor', 'order'] as const;
export const analysisConflictDeciderSchema = z.enum(ANALYSIS_CONFLICT_DECIDERS);
export type AnalysisConflictDecider = z.infer<typeof analysisConflictDeciderSchema>;

/** What each decider reads as on a screen. */
export const ANALYSIS_CONFLICT_DECIDER_LABELS: Readonly<Record<AnalysisConflictDecider, string>> = {
  symmetry: 'la symétrie l’emporte sur un objectif seul',
  floor: 'la plus haute des plus petites parts',
  order: 'l’ordre Pourquoi, Comment, Quoi',
};

/**
 * A CONFLICT (§6.3, G9): two levels, or three, fixed on the SAME function. The
 * code keeps the surest and sets the others aside; the conflict is recorded.
 */
export const analysisConflictSchema = z.object({
  function: analystFunctionSchema,
  kept: analystLevelSchema,
  dropped: z.array(analystLevelSchema).min(1),
  decidedBy: analysisConflictDeciderSchema,
});
export type AnalysisConflict = z.infer<typeof analysisConflictSchema>;

/* ------------------------------------------------------- candidates, eliminated */

/**
 * One of the six noyaux after the code has read the levels (§6.4, §6.5): a
 * candidate or an eliminated, the first FIXED level an eliminated one contradicts
 * (K9), its compatibility, and its style - null for an eliminated, which the
 * second pass does not note, and for a candidate whose style is absent.
 */
export const analysisNoyauSchema = z.object({
  noyau: noyauCodeSchema,
  candidate: z.boolean(),
  eliminatedBy: analystLevelSchema.nullable().default(null),
  compatibility: percentSchema,
  style: percentSchema.nullable().default(null),
});
export type AnalysisNoyau = z.infer<typeof analysisNoyauSchema>;

/* ------------------------------------------------------------ the second pass */

/** At most this many passages for a style, and as many against (§7.3, K7). */
export const ANALYST_PASSAGES_MAX = 8;

/** The bound of one passage a model quotes - met by a defect, never on purpose. */
export const ANALYST_PASSAGE_MAX = 2000;

/** Which side of a style a passage was quoted on. */
export const ANALYSIS_PASSAGE_SIDES = ['supporting', 'contradicting'] as const;
export const analysisPassageSideSchema = z.enum(ANALYSIS_PASSAGE_SIDES);
export type AnalysisPassageSide = z.infer<typeof analysisPassageSideSchema>;

const passageSchema = z.string().min(1).max(ANALYST_PASSAGE_MAX);

/**
 * The style of ONE candidate, after the code has checked its passages against
 * the person's answers (§7.4, K7): the note, the passages found - `supporting`
 * and `contradicting` - the passages NOT found, set aside and counted, and the
 * pass's sentence. `discardedCount` is the figure the parcours says; `discarded`
 * keeps what the model wrote, so the measure can see what it invents.
 *
 * `styleName` is the name Méthode gives this noyau's style in the version the
 * instrument was built with (« Conquête »): the parcours writes « style
 * Conquête » from here, and a later version of Méthode cannot change what a
 * determination said.
 *
 * Since #229 (§7.6, §10) a note of `analysis.style.notes` is the COMBINED note of a
 * candidate when the style was read several times: `score` is the mean of its
 * readings, rounded; the passages, the discarded passages and the sentence are those
 * of the reading that carries the note, whose number `reading` keeps. `reading` is
 * null at one reading, and inside the notes of a reading of `readings`, which are
 * whole as that reading wrote them.
 */
export const analysisStyleNoteSchema = z.object({
  noyau: noyauCodeSchema,
  styleName: z.string().max(METHOD_STYLE_NAME_MAX).default(''),
  score: percentSchema,
  supporting: z.array(passageSchema).max(ANALYST_PASSAGES_MAX).default([]),
  contradicting: z.array(passageSchema).max(ANALYST_PASSAGES_MAX).default([]),
  discarded: z
    .array(z.object({ side: analysisPassageSideSchema, text: passageSchema }))
    .max(ANALYST_PASSAGES_MAX * 2)
    .default([]),
  discardedCount: z.number().int().nonnegative().default(0),
  justification: z.string().min(1).max(NOYAU_JUSTIFICATION_MAX),
  reading: analystRunSchema.nullable().default(null),
});
export type AnalysisStyleNote = z.infer<typeof analysisStyleNoteSchema>;

/**
 * ONE reading of the style when it was read several times (§7.6, §10, Y1): its
 * number (`run`, 1 to N, in order), and either the whole notes it gave, one per
 * candidate, at the shape of `analysisStyleNoteSchema` - `rendered` -, or `failed`
 * when both its calls failed (§7.5).
 */
export const analysisStyleReadingSchema = z.discriminatedUnion('state', [
  z.object({
    run: analystRunSchema,
    state: z.literal('rendered'),
    notes: z.array(analysisStyleNoteSchema),
  }),
  z.object({ run: analystRunSchema, state: z.literal('failed') }),
]);
export type AnalysisStyleReading = z.infer<typeof analysisStyleReadingSchema>;

/**
 * The second pass, as the determination records it: `rendered` with one note per
 * candidate, or `absent` - every reading failed twice, the determination settled on
 * the compatibility alone, marked « style absent » (§7.5, G23).
 *
 * `readings` (#229, Y1) is EMPTY at one reading, the default, and otherwise holds
 * one entry per reading, in order - the style absent keeps its readings, all failed.
 * A style read several times and rendered on a part of its readings is `rendered`
 * with its `readings` saying which (`styleReadingsOf`).
 */
export const analysisStyleSchema = z.discriminatedUnion('state', [
  z.object({
    state: z.literal('rendered'),
    notes: z.array(analysisStyleNoteSchema),
    readings: z.array(analysisStyleReadingSchema).default([]),
  }),
  z.object({
    state: z.literal('absent'),
    readings: z.array(analysisStyleReadingSchema).default([]),
  }),
]);
export type AnalysisStyle = z.infer<typeof analysisStyleSchema>;

/* ------------------------------------------------------------------ the whole */

/**
 * THE analysis (§10). The header - `settings` (the thresholds, the weight and the
 * version of Méthode), `instrument`, `failure` - says what the determination was
 * read WITH; the rest says what the passes saw and the code made of it, and is
 * empty until the first pass has been read:
 *
 * - `functions` - the first pass, one row per objective it was served;
 * - `levels` - the three levels, in the order of a triplet;
 * - `conflicts` - the levels the code set aside, and why;
 * - `noyaux` - the six, in the order of the table, candidates and eliminated;
 * - `style` - the second pass, or its absence; null while it has not run.
 *
 * A determination settled `failed` WITHOUT a call (§9.2, G22) is the one case
 * where the header stands alone and `failure` names the reason.
 */
export const noyauAnalysisSchema = z.object({
  settings: analysisSettingsSchema,
  instrument: noyauInstrumentFingerprintSchema.nullable().default(null),
  failure: analysisFailureSchema.nullable().default(null),
  functions: z.array(analysisFunctionRowSchema).default([]),
  levels: z.array(analysisLevelSchema).max(3).default([]),
  conflicts: z.array(analysisConflictSchema).default([]),
  noyaux: z.array(analysisNoyauSchema).max(6).default([]),
  style: analysisStyleSchema.nullable().default(null),
});
export type NoyauAnalysis = z.infer<typeof noyauAnalysisSchema>;
